import { API, graphqlOperation } from 'aws-amplify';
import {
  createSession,
  getSession,
  updateSession,
  checkJoinSession,
  syncSessionById,
  syncStreamById,
  syncNewStreamBySession,
  updateSessionTeam,
  createSessionTeam,
  createLineup,
  updateLineup,
  syncLineupById,
} from '../../graphql/graphql';
import { createStreamWowza } from '../../graphql/mutations';
import { nanoid } from 'nanoid';
import { sessionStatus, accessType, genderType } from '../_constants';
import { sessionActions } from '../_actions';

export const sessionService = {
  join,
  create,
  load,
  update,
  streamRequest,
  subscribe,
  unsubscribe,
  subscribeStream,
  subscribeNewStream,
  addLineup,
  editLineup,
  subscribeLineup,
  updateProducer,
};

async function streamRequest(payload) {
  console.log(payload);
  return await API.graphql(
    graphqlOperation(createStreamWowza, { payload: JSON.stringify(payload) })
  );
}

async function load(key) {
  return await API.graphql(graphqlOperation(getSession, { sessionKey: key }));
}

async function join(key) {
  return await API.graphql(
    graphqlOperation(checkJoinSession, { sessionKey: key })
  );
}

// Createing a session handles session, sessionTeam assignments, sessionLineup creations
async function create(input, teamChange) {
  const payload = {
    sessionKey: nanoid(10),
    name: input.name,
    type: input.type,
    access: accessType.PRIVATE,
    startAt: input.startAt,
    status: sessionStatus.CREATED,
    gender: input.gender,
    competition: input.competition,
    judging: input.judging,
    judgingRequired: input.judgingRequired,
    apparatus: input.apparatus,
    //endAt: String
    //lineup: ??
  };

  const createSessionResponse = await API.graphql(
    graphqlOperation(createSession, { input: payload })
  );
  const session = createSessionResponse.data.createSession;

  const createSessionTeamsResponse = await Promise.all(
    teamChange.creates.map((input) => {
      return API.graphql(
        graphqlOperation(createSessionTeam, {
          input: {
            ...input,
            sessionId: session.id,
          },
        })
      );
    })
  );

  await Promise.all(
    teamChange.creates.map((input, i) => {
      return API.graphql(
        graphqlOperation(createLineup, {
          input: {
            sessionId: session.id,
            title: session.name,
            teamId: input.teamId,
            sessionTeamId:
              createSessionTeamsResponse[i].data.createSessionTeam.id,
            order: i,
          },
        })
      );
    })
  );

  return session;
}

async function update(input, teamChange = null) {
  const payload = {
    id: input.id,
    name: input.name,
    type: input.type,
    access: input.access,
    startAt: input.startAt,
    endAt: input.endAt ? input.endAt : null,
    gender: input.gender,
    competition: input.competition,
    judging: input.judging,
    judgingRequired: input.judgingRequired,
    apparatus: input.apparatus,
    _version: input._version,
    status: input.status,
  };

  const sessionUpdate = API.graphql(
    graphqlOperation(updateSession, { input: payload })
  );
  const updateBatch = teamChange
    ? Promise.all(
        teamChange.updates.map((input) => {
          return API.graphql(
            graphqlOperation(updateSessionTeam, { input: input })
          );
        })
      )
    : null;
  const createBatch = teamChange
    ? Promise.all(
        teamChange.creates.map((input) => {
          return API.graphql(
            graphqlOperation(createSessionTeam, { input: input })
          );
        })
      )
    : null;

  // Todo: need to sequence this to update the sessionlast to catch any changes to team relationships
  return await Promise.all([sessionUpdate, createBatch, updateBatch]);
}

async function subscribe(id, dispatch) {
  return await API.graphql(
    graphqlOperation(syncSessionById, { id: id })
  ).subscribe({
    next: (streamData) => {
      console.log(streamData);
      // TODO: need to figure out where data goes when you sync session
      dispatch(
        sessionActions.syncSession(streamData.value.data.subscribeToSession)
      );
    },
    error: (error) => {
      console.warn(error);
    },
  });
}

async function subscribeStream(id, channel, dispatch) {
  return await API.graphql(
    graphqlOperation(syncStreamById, { id: id })
  ).subscribe({
    next: (streamData) => {
      dispatch(
        sessionActions.syncStreamStatus(
          streamData.value.data.subscribeToStream,
          channel
        )
      );
    },
    error: (error) => {
      console.warn(error);
    },
  });
}

async function subscribeNewStream(id, dispatch) {
  return await API.graphql(
    graphqlOperation(syncNewStreamBySession, { sessionId: id })
  ).subscribe({
    next: (streamData) => {
      console.log(streamData);
      dispatch(
        sessionActions.syncNewStream(streamData.value.data.subscribeToNewStream)
      );
    },
    error: (error) => {
      console.warn(error);
    },
  });
}

function unsubscribe(sub) {
  if (sub) {
    sub.unsubscribe();
  }
  return Promise.resolve(true);
}

async function addLineup(input) {
  const createLineups = Promise.all(
    input.map((lineup) => {
      return API.graphql(graphqlOperation(createLineup, { input: lineup }));
    })
  );

  return await createLineups;
}

async function editLineup(input) {
  return await API.graphql(graphqlOperation(updateLineup, { input: input }));
}

async function subscribeLineup(id, dispatch) {
  return await API.graphql(
    graphqlOperation(syncLineupById, { id: id })
  ).subscribe({
    next: (lineupData) => {
      dispatch(
        sessionActions.syncLineup(lineupData.value.data.subscribeToLineup)
      );
    },
    error: (error) => {
      console.warn(error);
    },
  });
}

async function updateProducer(input) {
  const payload = {
    id: input.id,
    _version: input._version,
    producer: JSON.stringify(input.producer),
  };

  return await API.graphql(graphqlOperation(updateSession, { input: payload }));
}
